import { FunctionalComponent, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { CheckIcon } from "components/icons";

import { usePrevious } from "hooks/usePrevious";

import { WorkModel } from "models/WorkModel";

import style from "./style.scss";

interface BaseProps {
  isHidden: boolean;
  onChange: (work: WorkModel) => void;
  work: WorkModel;
}

interface RenderChildrenProps extends BaseProps {
  activeChild?: WorkModel;
  relationship: "children";
}

interface RenderParentsProps extends BaseProps {
  activeParent?: WorkModel;
  relationship: "parents";
}

type Props = RenderChildrenProps | RenderParentsProps;

type AnimationType = "arrow_toggle";

const ExhibitWorkConnector: FunctionalComponent<Props> = (props: Props) => {
  const [activeAnimations, setActiveAnimations] = useState<AnimationType[]>([]);
  const [isInitiallyVisible, setIsInitiallyVisible] = useState(false);
  const activeAnimationKeyframeMap = useRef(
    new Map</* keyframe Id= */ string, AnimationType>()
  );
  const animationPendingStart = useRef<AnimationType | undefined>();
  const prevProps = usePrevious(props);
  const rootElRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isInitiallyVisible) return;

    const revealTimeout = setTimeout(() => {
      setIsInitiallyVisible(true);
    }, 50);

    return () => {
      clearTimeout(revealTimeout);
    };
  });

  // Triggers the arrow animation if the active child changes.
  useEffect(() => {
    if (
      props.relationship === "children" &&
      props.activeChild !== (prevProps as RenderChildrenProps).activeChild
    ) {
      doAnimation("arrow_toggle");
    }
  });

  // Triggers the arrow animation if the active parent changes.
  useEffect(() => {
    if (
      props.relationship === "parents" &&
      props.activeParent !== (prevProps as RenderParentsProps).activeParent
    ) {
      doAnimation("arrow_toggle");
    }
  });

  return (
    <div
      class={style.root}
      data-active-animations={activeAnimations.join(" ")}
      data-child-work-active={String(
        !!(props.relationship === "children" && props.activeChild)
      )}
      data-hidden={String(props.isHidden)}
      data-parent-work-active={String(
        !!(props.relationship === "parents" && props.activeParent)
      )}
      data-relationship={props.relationship}
      data-initially-visible={String(isInitiallyVisible)}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}
      ref={rootElRef}
    >
      {props.relationship === "children" && props.work.childWorks.length > 0
        ? workChildren(props)
        : null}
      {props.relationship === "children" && props.work.childWorks.length === 0
        ? workTerminator()
        : null}
      {props.relationship === "parents" ? workParents(props) : null}
    </div>
  );

  function doAnimation(animationName: AnimationType) {
    const ongoing = activeAnimations ?? [];
    // If the animation is already occurring, we remove the class and add it
    // back on the next animation frame.
    setActiveAnimations(
      activeAnimations.filter((active) => active !== animationName)
    );
    requestAnimationFrame(() => {
      animationPendingStart.current = animationName;
      setActiveAnimations(ongoing.concat(animationName));
    });
  }

  function onAnimationStart(event: AnimationEvent) {
    if (animationPendingStart.current) {
      activeAnimationKeyframeMap.current.set(
        event.animationName,
        animationPendingStart.current
      );
    }
  }

  function onAnimationEnd(event: AnimationEvent) {
    const { animationName: keyframeId } = event;
    const animationName = activeAnimationKeyframeMap.current.get(keyframeId);

    if (animationName) {
      setActiveAnimations(
        activeAnimations.filter((active) => active !== animationName)
      );
    }
  }
};

// These are the controls coming FROM THE TOP (PARENT) WORK that change the
// path leading OUT OF this work
function workChildren(props: RenderChildrenProps) {
  const { work, onChange, activeChild } = props;
  const activeChildIndex = activeChild
    ? work.childWorks.indexOf(activeChild)
    : 0;
  const parentWorkChildCount = work.childWorks.length;

  if (parentWorkChildCount === 0) return null;

  return (
    <div class={style.worksContainer}>
      <div class={style.connectorLine} />
      <div class={style.connectorLineGrouper} />
      <div class={style.label}>
        {`Inspired ${parentWorkChildCount} ${
          parentWorkChildCount === 1 ? "artist" : "artists"
        }`}
      </div>
      <div class={style.buttonList} data-count={parentWorkChildCount}>
        {work.childWorks.map((parentChildWork) => (
          <button
            className={style.button}
            data-active={parentChildWork === activeChild}
            data-medium={parentChildWork.medium?.name?.toLowerCase()}
            onClick={() => {
              onChange(parentChildWork);
            }}
            key={parentChildWork.id}
            type="button"
          >
            {parentChildWork.medium?.name}
            {parentChildWork.visited ? (
              <span
                class={style.buttonVisitedIndicator}
                title="You viewed this work"
              >
                <CheckIcon className={style.buttonVisitedIndicatorIcon} />
              </span>
            ) : null}
          </button>
        ))}
        <div class={style.arrow} data-position={activeChildIndex + 1}>
          {arrowIcon()}
        </div>
      </div>
    </div>
  );
}

// These are the controls coming FROM THE BOTTOM (CHILD) WORK that change the
// path leading UP TO this work.
function workParents(props: RenderParentsProps) {
  const { work, onChange, activeParent } = props;
  const activeParentIndex = activeParent
    ? work.parentWorks.indexOf(activeParent)
    : 0;
  const childWorkParentCount = work.parentWorks.length;

  if (childWorkParentCount === 0) return null;

  return (
    <div class={style.worksContainer}>
      <div class={style.buttonList} data-count={childWorkParentCount}>
        {work.parentWorks.map((childParentWork) => (
          <button
            className={style.button}
            data-active={childParentWork === activeParent}
            data-medium={childParentWork.medium?.name?.toLowerCase()}
            onClick={() => {
              onChange(childParentWork);
            }}
            key={childParentWork.id}
            type="button"
          >
            {childParentWork.medium?.name}
            {childParentWork.visited ? (
              <span
                class={style.buttonVisitedIndicator}
                title="You viewed this work"
              >
                <CheckIcon className={style.buttonVisitedIndicatorIcon} />
              </span>
            ) : null}
          </button>
        ))}
        <div class={style.arrow} data-position={activeParentIndex + 1}>
          {arrowIcon()}
        </div>
      </div>
      <div class={style.connectorLineGrouper} />
      <div class={style.connectorLine} />
      <div class={style.label}>
        {`Inspired by ${childWorkParentCount} ${
          childWorkParentCount === 1 ? "artist" : "artists"
        }`}
      </div>
    </div>
  );
}

function workTerminator() {
  return (
    <div class={style.worksContainer}>
      <div class={style.connectorLine} />
      <div class={style.connectorLineGrouper} />
      <div class={style.label}>TELEPHONE path complete</div>
    </div>
  );
}

function arrowIcon() {
  return (
    <svg width="10" height="19" viewBox="0 0 10 19">
      <path
        d="M6 15.5858L8.29289 13.2929C8.68342 12.9024 9.31658 12.9024 9.70711 13.2929C10.0976 13.6834 10.0976 14.3166 9.70711 14.7071L5.70711 18.7071C5.31658 19.0976 4.68342 19.0976 4.29289 18.7071L0.292893 14.7071C-0.0976311 14.3166 -0.0976311 13.6834 0.292893 13.2929C0.683417 12.9024 1.31658 12.9024 1.70711 13.2929L4 15.5858L4 0H6L6 15.5858Z"
        fill="black"
      />
    </svg>
  );
}

export default ExhibitWorkConnector;
